import {
  getPickupPointFieldInputValue,
  readPickupPointFromAdditionalField,
  setPickupPointFieldInputValue,
} from '../additionalFields.js'
import {
  ensureCheckoutDom,
  getMapIframeElement,
  getPostnummerInputElement,
  getPostnummerInputs,
  hideMapPopup,
  hasMapButtonElement,
  isMapButtonElement,
  isMapCloseButtonElement,
  mapTemplateId,
  openMapPopup,
  setPickupPointPresentation,
} from '../dom.js'
import {
  getSelectedPaymentDebugData,
} from '../payment.js'
import {
  normalizePointType,
  pickupPointIdentifier,
  requiresGermanPostnummer,
  serializePickupPointFieldValue,
  withPickupPointCountry,
} from '../pickupPoint.js'
import {
  acceptPickupPointMessage,
  buildCheckoutMapUrl,
  closeMapSession,
  createMapSession,
} from '../map.js'
import { createCheckoutContext } from '../context.js'

export function initializeStorefrontCheckout({ bootstrap: checkoutBootstrap, configLoader: checkoutConfigLoader } = {}) {

  const octolizeDhl = (window.octolizeDhl = window.octolizeDhl || {})
  const logPrefix = octolizeDhl.logPrefix || '[DHL checkout]'

  function warn(...args) {
    console.warn(logPrefix, ...args)
  }

  function error(...args) {
    console.error(logPrefix, ...args)
  }

  let basketShippingsApi = null
  let basketAdditionalFieldsApi = null
  let basketOrderApi = null
  let basketPaymentsApi = null
  let flashMessengerApi = null
  let selectedPickupPointSnapshot = null
  let pickupPointSelectionCleared = false
  let lastPickupValidationFailure = null
  let lastPaymentIsCashOnDelivery = false
  let paymentSyncTimeouts = []
  let latestPickupPointFieldValue = null
  let pickupPointFieldWriteInFlight = null
  let postnummerPersistTimeoutId = null
  const pickupMapStateSyncTimeoutIds = new Set()
  const pickupMapStateSyncDelays = [0, 150, 500, 1000]
  const checkoutShippingRefreshEvents = [
    'basket.basketUpdated',
    'PageManager.rendered',
  ]
  let selectedShippingId = null
  let selectedShippingCountry = ''
  let shippingsWithMapButton = new WeakSet()
  let mapSession = null

  function ensureBootstrapConfiguration() {
    if (!octolizeDhl.appUrl && checkoutBootstrap.appUrl) {
      octolizeDhl.appUrl = checkoutBootstrap.appUrl
    }

    if (!octolizeDhl.checkoutConfigUrl && checkoutBootstrap.configUrl) {
      octolizeDhl.checkoutConfigUrl = checkoutBootstrap.configUrl
    }
  }

  ensureBootstrapConfiguration()
  ensureCheckoutDom()

  function getSelectedShippingId() {
    const selectedShipping = basketShippingsApi?.getSelectedShipping?.()
    if (!selectedShipping) {
      return null
    }

    const shippingId = Number.parseInt(String(selectedShipping.id), 10)

    return Number.isFinite(shippingId) ? shippingId : null
  }

  function getSelectedShippingCountryIsoCode() {
    const isocode = basketShippingsApi?.getSelectedShippingCountry?.()?.country?.isocode ?? ''
    return String(isocode).trim().toUpperCase()
  }

  function isPickupShipping(shipmentSettings, shippingId) {
    return Number.isInteger(shippingId)
      && (shipmentSettings?.pickup ?? []).some((pickupShippingId) => Number.parseInt(String(pickupShippingId), 10) === shippingId)
  }

  function isPostnummerRequiredForCurrentShipping(_shipmentSettings, pickupPoint = selectedPickupPointSnapshot) {
    return requiresGermanPostnummer(pickupPoint, getSelectedShippingCountryIsoCode())
  }

  function setCurrentPickupPointPresentation(pickupPoint, shipmentSettings) {
    setPickupPointPresentation(pickupPoint, isPostnummerRequiredForCurrentShipping(shipmentSettings))
  }

  function withSelectedShippingCountry(pickupPoint) {
    return withPickupPointCountry(pickupPoint, getSelectedShippingCountryIsoCode())
  }

  function serializeCurrentPickupPoint(pickupPoint) {
    return serializePickupPointFieldValue(pickupPoint, getSelectedShippingCountryIsoCode())
  }

  function getCurrentCheckoutContext() {
    return createCheckoutContext({
      stage: 'storefront',
      shippingId: getSelectedShippingId(),
      country: getSelectedShippingCountryIsoCode(),
      paymentType: isCurrentPaymentCashOnDelivery(),
    })
  }

  function closeMapPopup() {
    closeMapSession(mapSession)
    mapSession = null
    hideMapPopup()
  }

  async function resolveShipmentSettings() {
    if (octolizeDhl.shipmentSettings && typeof octolizeDhl.shipmentSettings === 'object') {
      return octolizeDhl.shipmentSettings
    }

    if (octolizeDhl.checkoutConfigPromise instanceof Promise) {
      const shipmentSettings = await octolizeDhl.checkoutConfigPromise
      if (shipmentSettings && typeof shipmentSettings === 'object') {
        octolizeDhl.shipmentSettings = shipmentSettings
      } else {
        octolizeDhl.checkoutConfigPromise = null
      }

      return octolizeDhl.shipmentSettings || null
    }

    if (typeof octolizeDhl.checkoutConfigUrl !== 'string' || octolizeDhl.checkoutConfigUrl === '') {
      warn('Missing checkoutConfigUrl, shipment settings cannot be resolved')
      return null
    }

    octolizeDhl.checkoutConfigPromise = checkoutConfigLoader.load()
      .then((shipmentSettings) => {
        if (shipmentSettings && typeof shipmentSettings === 'object') {
          octolizeDhl.shipmentSettings = shipmentSettings
          return shipmentSettings
        }

        warn('checkoutConfigUrl returned empty or invalid payload')
        octolizeDhl.checkoutConfigPromise = null
        return null
      })

    return octolizeDhl.checkoutConfigPromise
  }

  function readPickupPointFromField(shipmentSettings) {
    if (pickupPointSelectionCleared) {
      return null
    }

    const pickupPoint = readPickupPointFromAdditionalField(shipmentSettings, basketAdditionalFieldsApi)

    if (pickupPoint) {
      selectedPickupPointSnapshot = withSelectedShippingCountry(pickupPoint)
      return selectedPickupPointSnapshot
    }

    selectedPickupPointSnapshot = null
    return null
  }

  function isCurrentPaymentCashOnDelivery() {
    return getSelectedPaymentDebugData(basketPaymentsApi).isCashOnDelivery
  }

  async function savePickupPointSelection(shipmentSettings, pickupPoint) {
    if (!shipmentSettings?.pickupPointFieldId || !basketAdditionalFieldsApi?.change) {
      warn('Cannot save pickup point, missing pickupPointFieldId or basketAdditionalFieldsApi', {
        pickupPointFieldId: shipmentSettings?.pickupPointFieldId ?? null,
      })
      return false
    }

    const pointId = pickupPointIdentifier(pickupPoint)
    const serializedPickupPoint = serializeCurrentPickupPoint(pickupPoint)

    pickupPointSelectionCleared = false
    latestPickupPointFieldValue = serializedPickupPoint
    setPickupPointFieldInputValue(shipmentSettings, serializedPickupPoint)

    try {
      await basketAdditionalFieldsApi.change(
        shipmentSettings.pickupPointFieldId,
        serializedPickupPoint,
      )
      if (latestPickupPointFieldValue === serializedPickupPoint) {
        setPickupPointFieldInputValue(shipmentSettings, serializedPickupPoint)
      }
      selectedPickupPointSnapshot = withSelectedShippingCountry(pickupPoint)
      setCurrentPickupPointPresentation(pickupPoint, shipmentSettings)
      return true
    } catch (changeError) {
      selectedPickupPointSnapshot = null
      pickupPointSelectionCleared = true
      setPickupPointFieldInputValue(shipmentSettings, '')
      setCurrentPickupPointPresentation(null, shipmentSettings)
      error('Failed to save pickup point to hidden field', {
        pickupPointFieldId: shipmentSettings.pickupPointFieldId,
        pointId,
        changeError,
      })
      return false
    }
  }

  async function clearPickupPointSelection(shipmentSettings, reason = 'unknown') {
    void reason
    const hasSavedPickupPoint = Boolean(
      selectedPickupPointSnapshot
      || readPickupPointFromAdditionalField(shipmentSettings, basketAdditionalFieldsApi)
      || getPickupPointFieldInputValue(shipmentSettings),
    )

    if (!hasSavedPickupPoint) {
      pickupPointSelectionCleared = true
      flashMessengerApi?.removeAllFlashMessages?.('shipping-chooser')
      flashMessengerApi?.removeAllFlashMessages?.('global')
      lastPickupValidationFailure = null
      setCurrentPickupPointPresentation(null, shipmentSettings)
      return
    }

    selectedPickupPointSnapshot = null
    pickupPointSelectionCleared = true
    latestPickupPointFieldValue = ''
    setPickupPointFieldInputValue(shipmentSettings, '')
    setCurrentPickupPointPresentation(null, shipmentSettings)

    if (shipmentSettings?.pickupPointFieldId && basketAdditionalFieldsApi?.change) {
      try {
        await basketAdditionalFieldsApi.change(shipmentSettings.pickupPointFieldId, '')
      } catch (changeError) {
        warn('Could not clear pickup point field', {
          pickupPointFieldId: shipmentSettings.pickupPointFieldId,
          changeError,
        })
      }
    }
    setPickupPointFieldInputValue(shipmentSettings, '')

    flashMessengerApi?.removeAllFlashMessages?.('shipping-chooser')
    flashMessengerApi?.removeAllFlashMessages?.('global')
    lastPickupValidationFailure = null
    setCurrentPickupPointPresentation(null, shipmentSettings)
  }

  async function syncPaymentState(shipmentSettings) {
    const paymentDebugData = getSelectedPaymentDebugData(basketPaymentsApi)
    const paymentIsCashOnDelivery = paymentDebugData.isCashOnDelivery

    if (paymentIsCashOnDelivery !== lastPaymentIsCashOnDelivery) {
      closeMapPopup()
    }

    if (paymentIsCashOnDelivery && !lastPaymentIsCashOnDelivery) {
      await clearPickupPointSelection(shipmentSettings, 'payment-changed-to-cod')
    }

    lastPaymentIsCashOnDelivery = paymentIsCashOnDelivery
  }

  function schedulePaymentStateSync(shipmentSettings) {
    paymentSyncTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId))
    paymentSyncTimeouts = [
      window.setTimeout(() => {
        syncPaymentState(shipmentSettings).catch((syncError) => {
          error('Failed to synchronize payment state', syncError)
        })
      }, 100),
      window.setTimeout(() => {
        syncPaymentState(shipmentSettings).catch((syncError) => {
          error('Failed to synchronize payment state', syncError)
        })
      }, 500),
    ]
  }

  function showMapPopup(shipmentSettings) {
    const context = getCurrentCheckoutContext()
    if (context.shippingId === null) {
      warn('Cannot open pickup point modal, no shipping selected')
      return
    }

    const mapUrl = buildCheckoutMapUrl(shipmentSettings, context)

    if (!mapUrl) {
      warn('Cannot open pickup point modal, empty mapUrl', {
        shippingId: context.shippingId,
        shipmentSettings,
      })
      return
    }

    ensureCheckoutDom()
    closeMapPopup()
    if (!openMapPopup(mapUrl.toString())) {
      warn('Cannot open pickup point modal, popup or iframe is missing')
      return
    }

    const iframe = getMapIframeElement()
    mapSession = createMapSession({ iframe, origin: mapUrl.origin, context, pointType: mapUrl.searchParams.get('ptype') })
  }

  function addShippingInfoToShippings(shipmentSettings) {
    const pickupShippings = shipmentSettings?.pickup ?? []
    const basketShippings = basketShippingsApi?.get?.() ?? []

    pickupShippings.forEach((shippingId) => {
      const shipping = basketShippings.find((entry) => Number.parseInt(String(entry.id), 10) === shippingId)
      if (shipping && !shippingsWithMapButton.has(shipping)) {
        basketShippingsApi.addShippingInfo(shipping, mapTemplateId)
        shippingsWithMapButton.add(shipping)
      }
    })
  }

  function resetRegisteredMapButtons() {
    shippingsWithMapButton = new WeakSet()
  }

  function clearScheduledPickupMapStateSyncs() {
    pickupMapStateSyncTimeoutIds.forEach((timeoutId) => {
      window.clearTimeout(timeoutId)
    })
    pickupMapStateSyncTimeoutIds.clear()
  }

  async function syncPickupMapState(shipmentSettings, refreshMissingMapButton = false) {
    const currentShippingId = getSelectedShippingId()
    const currentShippingCountry = getSelectedShippingCountryIsoCode()
    const shippingChanged = currentShippingId !== selectedShippingId
    const countryChanged = currentShippingCountry !== selectedShippingCountry

    if (refreshMissingMapButton && !hasMapButtonElement()) {
      resetRegisteredMapButtons()
    }

    addShippingInfoToShippings(shipmentSettings)

    selectedShippingId = currentShippingId
    selectedShippingCountry = currentShippingCountry

    if (shippingChanged || countryChanged) {
      closeMapPopup()
      await clearPickupPointSelection(
        shipmentSettings,
        countryChanged ? 'shipping-country-changed' : 'shipping-method-changed',
      )
      return
    }

    await syncButtonLabelFromField(shipmentSettings)
  }

  function schedulePickupMapStateSync(shipmentSettings, delays = [0], refreshMissingMapButton = false) {
    clearScheduledPickupMapStateSyncs()

    delays.forEach((delay) => {
      const timeoutId = window.setTimeout(() => {
        pickupMapStateSyncTimeoutIds.delete(timeoutId)
        syncPickupMapState(shipmentSettings, refreshMissingMapButton).catch((syncError) => {
          error('Failed to synchronize pickup map state', syncError)
        })
      }, delay)

      pickupMapStateSyncTimeoutIds.add(timeoutId)
    })
  }

  async function syncButtonLabelFromField(shipmentSettings) {
    if (!shipmentSettings?.pickupPointFieldId || !basketAdditionalFieldsApi?.get) {
      warn('Cannot sync pickup point label, missing pickupPointFieldId or basketAdditionalFieldsApi')
      setCurrentPickupPointPresentation(null, shipmentSettings)
      return
    }

    const pickupPoint = readPickupPointFromField(shipmentSettings)

    setCurrentPickupPointPresentation(pickupPoint, shipmentSettings)
  }

  function addValidationMessage(shipmentSettings) {
    if (!flashMessengerApi) {
      warn('Cannot add validation message, FlashMessengerApi is not available')
      return
    }

    const addPickupValidationFlashMessage = (content) => {
      const message = {
        content,
        type: 'error',
        isCloseable: false,
        timeout: 0,
      }

      flashMessengerApi.addFlashMessage(message, 'shipping-chooser')
      flashMessengerApi.addFlashMessage(message, 'global')
    }

    if (!shipmentSettings?.pickupPointFieldId) {
      addPickupValidationFlashMessage('Blad w konfiguracji sklepu: brak dodatkowego pola wyboru punktu odbioru.')

      return
    }

    if (lastPickupValidationFailure === 'postnummer') {
      addPickupValidationFlashMessage('Dla dostawy do punktu odbioru DHL w Niemczech prosze podac Postnummer.')

      return
    }

    if (lastPickupValidationFailure === 'pointType') {
      addPickupValidationFlashMessage('Nie udalo sie potwierdzic typu niemieckiego punktu DHL. Wybierz punkt ponownie.')

      return
    }

    addPickupValidationFlashMessage('Prosze wybrac punkt odbioru.')
  }

  function validatePickupPointSelection(shipmentSettings) {
    const shippingId = getSelectedShippingId()
    if (shippingId === null || !isPickupShipping(shipmentSettings, shippingId)) {
      lastPickupValidationFailure = null
      return true
    }

    if (!shipmentSettings?.pickupPointFieldId || !basketAdditionalFieldsApi?.get) {
      warn('Pickup point validation failed, missing pickupPointFieldId or basketAdditionalFieldsApi', {
        shippingId,
        pickupPointFieldId: shipmentSettings?.pickupPointFieldId ?? null,
      })
      lastPickupValidationFailure = 'pickup'
      return false
    }

    const pickupPoint = readPickupPointFromField(shipmentSettings)
    const pointId = pickupPointIdentifier(pickupPoint)
    if (pointId === '') {
      lastPickupValidationFailure = 'pickup'
      return false
    }

    if (
      getSelectedShippingCountryIsoCode() === 'DE'
      && !['packStation', 'parcelShop'].includes(normalizePointType(pickupPoint.type))
    ) {
      lastPickupValidationFailure = 'pointType'
      return false
    }

    if (requiresGermanPostnummer(pickupPoint, getSelectedShippingCountryIsoCode())) {
      const postnummer = String(pickupPoint?.postnummer ?? '').trim()
      if (postnummer === '') {
        lastPickupValidationFailure = 'postnummer'
        return false
      }
    }

    lastPickupValidationFailure = null
    return true
  }

  function addValidation(shipmentSettings) {
    basketOrderApi.addOrderValidation(
      () => validatePickupPointSelection(shipmentSettings),
      () => addValidationMessage(shipmentSettings),
    )
  }

  function waitForStorefront(startedAt = Date.now()) {
    return new Promise((resolve) => {
      const checkAvailability = () => {
        if (typeof window.useStorefront === 'function') {
          resolve(true)
          return
        }

        if (Date.now() - startedAt >= 5000) {
          warn('Storefront API was not available within the initialization timeout')
          resolve(false)
          return
        }

        window.setTimeout(checkAvailability, 100)
      }

      checkAvailability()
    })
  }

  async function handlePickupPointMessage(event, shipmentSettings) {
    const pickupPoint = acceptPickupPointMessage(event, mapSession, getCurrentCheckoutContext())
    const pointId = pickupPointIdentifier(pickupPoint)
    if (pointId === '') {
      return
    }

    if (!requiresGermanPostnummer(pickupPoint, getSelectedShippingCountryIsoCode())) {
      pickupPoint.postnummer = ''
    } else if (!pickupPoint.postnummer) {
      const existingPostnummer =
        String(selectedPickupPointSnapshot?.postnummer ?? '').trim() ||
        String(getPostnummerInputs()[0]?.value ?? '').trim()
      if (existingPostnummer !== '') {
        pickupPoint.postnummer = existingPostnummer
      }
    }

    closeMapPopup()
    flashMessengerApi?.removeAllFlashMessages?.('shipping-chooser')
    flashMessengerApi?.removeAllFlashMessages?.('global')

    await savePickupPointSelection(shipmentSettings, pickupPoint)
  }

  async function persistLatestPickupPointFieldValue(shipmentSettings, { updateInputChangeEvent = false } = {}) {
    if (!shipmentSettings?.pickupPointFieldId || !basketAdditionalFieldsApi?.change || latestPickupPointFieldValue === null) {
      return
    }

    if (pickupPointFieldWriteInFlight) {
      await pickupPointFieldWriteInFlight
      return
    }

    pickupPointFieldWriteInFlight = (async () => {
      while (latestPickupPointFieldValue !== null) {
        const serializedValue = latestPickupPointFieldValue

        try {
          await basketAdditionalFieldsApi.change(
            shipmentSettings.pickupPointFieldId,
            serializedValue,
          )
        } catch (changeError) {
          error('Failed to save postnummer to pickup point field', {
            pickupPointFieldId: shipmentSettings.pickupPointFieldId,
            changeError,
          })
          break
        }

        if (latestPickupPointFieldValue !== serializedValue) {
          continue
        }

        setPickupPointFieldInputValue(shipmentSettings, serializedValue, { dispatchChange: updateInputChangeEvent })
        latestPickupPointFieldValue = null
        break
      }
    })()

    try {
      await pickupPointFieldWriteInFlight
    } finally {
      pickupPointFieldWriteInFlight = null
    }
  }

  function schedulePostnummerPersist(shipmentSettings) {
    if (postnummerPersistTimeoutId !== null) {
      window.clearTimeout(postnummerPersistTimeoutId)
    }

    postnummerPersistTimeoutId = window.setTimeout(() => {
      postnummerPersistTimeoutId = null
      persistLatestPickupPointFieldValue(shipmentSettings).catch((persistError) => {
        error('Failed to debounce postnummer save', persistError)
      })
    }, 500)
  }

  async function handlePostnummerInput(inputElement, shipmentSettings) {
    const typedPostnummer = String(inputElement.value ?? '').trim()
    selectedPickupPointSnapshot = withSelectedShippingCountry({
      ...(selectedPickupPointSnapshot ?? {}),
      postnummer: typedPostnummer,
    })

    if (!shipmentSettings?.pickupPointFieldId) {
      return
    }

    const serializedPickupPoint = serializeCurrentPickupPoint(selectedPickupPointSnapshot)
    latestPickupPointFieldValue = serializedPickupPoint
    setPickupPointFieldInputValue(shipmentSettings, serializedPickupPoint, { dispatchChange: false })
    schedulePostnummerPersist(shipmentSettings)

    if (typedPostnummer !== '' && lastPickupValidationFailure === 'postnummer') {
      flashMessengerApi?.removeAllFlashMessages?.('shipping-chooser')
      flashMessengerApi?.removeAllFlashMessages?.('global')
    }
  }

  async function handlePostnummerBlur(inputElement, shipmentSettings) {
    await handlePostnummerInput(inputElement, shipmentSettings)

    if (postnummerPersistTimeoutId !== null) {
      window.clearTimeout(postnummerPersistTimeoutId)
      postnummerPersistTimeoutId = null
    }

    await persistLatestPickupPointFieldValue(shipmentSettings)
  }

  async function initializeStorefrontLogic() {
    const shipmentSettings = await resolveShipmentSettings()
    if (!shipmentSettings) {
      warn('Storefront initialization aborted, shipmentSettings are unavailable')
      return null
    }

    window.useStorefront(async (storefront) => {
      flashMessengerApi = storefront.getApiSync('FlashMessengerApi')
    })

    window.useStorefront(async ({ eventBus, getApi }) => {
      basketShippingsApi = await getApi('basketShippingsApi')
      basketAdditionalFieldsApi = await getApi('basketAdditionalFieldsApi')
      basketOrderApi = await getApi('basketOrderApi')
      try {
        basketPaymentsApi = await getApi('basketPaymentsApi')
      } catch (paymentsApiError) {
        warn('basketPaymentsApi is unavailable, falling back to DOM payment lookup', paymentsApiError)
      }

      addShippingInfoToShippings(shipmentSettings)
      addValidation(shipmentSettings)
      selectedShippingId = getSelectedShippingId()
      selectedShippingCountry = getSelectedShippingCountryIsoCode()
      lastPaymentIsCashOnDelivery = isCurrentPaymentCashOnDelivery()
      await syncButtonLabelFromField(shipmentSettings)
      schedulePickupMapStateSync(shipmentSettings, pickupMapStateSyncDelays)

      window.addEventListener('message', async (event) => {
        await handlePickupPointMessage(event, shipmentSettings)
      })

      document.addEventListener('click', (event) => {
        if (isMapButtonElement(event.target)) {
          showMapPopup(shipmentSettings)
        }

        if (isMapCloseButtonElement(event.target)) {
          event.preventDefault()
          closeMapPopup()
        }
      })

      document.addEventListener('input', async (event) => {
        const inputElement = getPostnummerInputElement(event.target)
        if (!inputElement) {
          return
        }

        await handlePostnummerInput(inputElement, shipmentSettings)
      })

      document.addEventListener('blur', async (event) => {
        const inputElement = getPostnummerInputElement(event.target)
        if (!inputElement) {
          return
        }

        await handlePostnummerBlur(inputElement, shipmentSettings)
      }, true)

      const scheduleCheckoutShippingRefresh = () => {
        closeMapPopup()
        schedulePickupMapStateSync(shipmentSettings, pickupMapStateSyncDelays, true)
      }

      checkoutShippingRefreshEvents.forEach((eventName) => {
        eventBus.on(eventName, scheduleCheckoutShippingRefresh)
      })

      eventBus.on('basket.setShipping', () => {
        closeMapPopup()
        schedulePickupMapStateSync(shipmentSettings)
      })
      eventBus.on('basket.setShippingCountry', scheduleCheckoutShippingRefresh)
      document.addEventListener('change', scheduleCheckoutShippingRefresh, true)

      eventBus.on('basket.setPayment', async () => {
        await syncPaymentState(shipmentSettings)
        schedulePaymentStateSync(shipmentSettings)
      })
    })

    return { adapter: 'storefront' }
  }

  return waitForStorefront().then((available) => available ? initializeStorefrontLogic() : null)
}
